import { useMutation, useQuery } from "@apollo/client";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogContentText from "@mui/material/DialogContentText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  HarmonizedViolationDuplicateCandidatesQuery,
  HarmonizedViolationDuplicatesQuery,
  HarmonizedViolationGovStatus,
  HarmonizedViolationProgram,
} from "generated-graphql/graphql";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2/omnisearchStore";
import {
  HARMONIZED_VIOLATION_DUPLICATE_CANDIDATES,
  HARMONIZED_VIOLATION_DUPLICATES,
  HARMONIZED_VIOLATION_STATS,
  HARMONIZED_VIOLATIONS,
  SET_HARMONIZED_VIOLATION_DUPLICATE_OF,
} from "hooks/useHarmonizedViolations";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { getUserFacingMessage } from "utils/errorUtils";

import { type ViolationRow } from "./columns";
import { GOV_STATUS_LABEL, SectionHeading } from "./violationDisplay";
import { duplicatesOfOmnisearch } from "./violationOmnisearch";

/** Quarter of a violation's `violationStartDate`; null when there is no date to compare. */
function reportingPeriod(date: string | null | undefined): string | null {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.getUTCFullYear()}-Q${Math.floor(parsed.getUTCMonth() / 3) + 1}`;
}

/**
 * Whether two violations sit in different quarters — usually a recurring failure
 * rather than one event seen twice, which is the case the no-action closure exists
 * for. The only case the mark pauses to confirm; a missing date on either side
 * proves nothing, so it does not.
 */
export function inDifferentReportingPeriods(
  subjectDate: string | null | undefined,
  candidateDate: string | null | undefined
): boolean {
  const subjectPeriod = reportingPeriod(subjectDate);
  const candidatePeriod = reportingPeriod(candidateDate);
  return Boolean(subjectPeriod && candidatePeriod && subjectPeriod !== candidatePeriod);
}

/**
 * How many candidates one page holds. AsyncAutocomplete floors its page size at
 * 25, so a smaller value here would not be honoured.
 */
export const CANDIDATE_PAGE_SIZE = 25;

/**
 * Empty, so the ordering is the server's default (highest severity, most
 * recently seen) — the same order the grid shows. Hoisted because
 * AsyncAutocomplete memoizes on this prop's identity.
 */
const CANDIDATE_SORT_MODEL: GridSortModel = [];

/** One offered canonical: what the picker labels, writes, and dates. */
export type CanonicalCandidate =
  HarmonizedViolationDuplicateCandidatesQuery["harmonizedViolationDuplicateCandidates"]["items"][number];

/**
 * The whole page, exactly as the server ordered it — `facilityId`'s own rows
 * lead, which is what the picker is for. Ordering server-side rather than here
 * is the point: a per-page client sort can only promote same-facility rows that
 * already reached the page, and the server's order is facility-agnostic, so at a
 * facility with more violations than one page none of its own rows ever arrived
 * to promote.
 *
 * Never filtered. Rows that cannot be the canonical are disabled rather than
 * dropped, because the autocomplete decides whether more pages exist from the
 * length of what this returns — so removing any would short the page and end
 * paging early.
 */
function candidateItems(data: HarmonizedViolationDuplicateCandidatesQuery): CanonicalCandidate[] {
  return data.harmonizedViolationDuplicateCandidates.items;
}

/** Enough to tell two violations apart: which violation, where, and what it was. */
export function candidateLabel(candidate: {
  violationId: string;
  violationDescription?: string | null;
  facility: { name: string };
}): string {
  return [candidate.violationId, candidate.facility.name, candidate.violationDescription]
    .filter(Boolean)
    .join(" — ");
}

/**
 * Rows that may not be the canonical: `ids`, each labelled with `note` saying
 * why. Required, not optional: a picker that offers a row the server will refuse
 * dead-ends the user, who has no way to tell which row to drop. They stay in the
 * list and are DISABLED rather than filtered out — the
 * autocomplete decides whether more pages exist from how many options a page
 * returns, so dropping any would short the page and end paging early, leaving
 * the canonical a user wants unreachable with more than one page of candidates.
 */
export type CanonicalExclusion = {
  ids: readonly string[];
  note: string;
  /**
   * The same exclusion as a clause, for the empty-list text — "and not this
   * violation itself" / "and not one of the violations you are marking".
   */
  constraint: string;
};

/**
 * Why the list can come up empty, in place of the autocomplete's "No options
 * found." Both dead ends are a rule the user cannot see from the grid: a
 * duplicate only ever points at another violation in the SAME EPA program, and
 * never at one already marked a duplicate. Phrased as what cannot be PICKED
 * rather than what was not found, because excluded matches are listed (disabled,
 * with their note) rather than hidden — so "nothing matched" would be untrue
 * exactly when a user typed a prefix that hits only the rows they are marking.
 */
function noCandidatesText(program: HarmonizedViolationProgram | null, constraint: string): string {
  const named = program ? `${prettyPrintEnumValue(program)} ` : "";
  const sameProgram = program
    ? `another ${prettyPrintEnumValue(program)} violation`
    : "another violation in the same EPA program (air, waste, or water)";
  return `No ${named}violation here can be the canonical. A violation can only be a duplicate of ${sameProgram} that is not already a duplicate itself, ${constraint}.`;
}

/**
 * Picks the canonical violation a row (or a bulk selection) duplicates. Shared
 * by the detail drawer's editor and the grid's bulk dialog, so both search the
 * same candidate universe: the dedicated candidate query, which enforces the
 * grid's tenant scope, facility access, and held-row suppression, and drops rows
 * that are themselves duplicates.
 *
 * The search is always tenant-wide. `facilityId` orders it instead of narrowing
 * it — server-side, so the subject's own facility leads the FIRST page rather
 * than only whichever of its rows a facility-agnostic order happened to include.
 * Null (a bulk selection spanning facilities) leaves the default order alone.
 * `program` DOES narrow it: a duplicate only ever lives in its own EPA program,
 * so offering another program's rows would dead-end the user on a server
 * refusal. `exclude` names the rows that may not be the canonical — the drawer's
 * subject row, or a bulk selection — and the `note` shown against each.
 */
export function CanonicalPicker({
  tenantId,
  facilityId,
  program,
  exclude,
  value,
  onChange,
  disabled,
}: {
  tenantId: string;
  facilityId: string | null;
  program: HarmonizedViolationProgram | null;
  exclude: CanonicalExclusion;
  value: CanonicalCandidate | null;
  onChange: (candidate: CanonicalCandidate | null) => void;
  disabled?: boolean;
}) {
  const [pickerOpened, setPickerOpened] = useState(false);
  const variables = useMemo(
    () => ({ tenantId, program, facilityId }),
    [tenantId, program, facilityId]
  );
  const excludedIds = useMemo(() => new Set(exclude.ids), [exclude]);
  // Memoized: the autocomplete resets its options whenever this prop's identity
  // changes, and the page needs no reordering now that the server orders it.
  const getItems = useCallback(candidateItems, []);

  return (
    <AsyncAutocomplete
      query={HARMONIZED_VIOLATION_DUPLICATE_CANDIDATES}
      variables={variables}
      initialSortModel={CANDIDATE_SORT_MODEL}
      initialPageSize={CANDIDATE_PAGE_SIZE}
      getItems={getItems}
      noOptionsText={noCandidatesText(program, exclude.constraint)}
      getOptionLabel={candidateLabel}
      getOptionDisabled={(option) => excludedIds.has(option.id)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {candidateLabel(option)}
          {excludedIds.has(option.id) ? ` ${exclude.note}` : ""}
        </li>
      )}
      placeholderLabel="Duplicate"
      hideLabel
      value={value}
      onChange={onChange}
      // Lazy until the picker is first opened, then kept live so reopening the
      // popup shows the page already loaded rather than searching again.
      lazyLoad={!pickerOpened}
      onOpen={() => setPickerOpened(true)}
      disabled={disabled}
      textFieldProps={{ size: "small", placeholder: "No duplicate marked" }}
      // No validation hangs off this field, so the helper-text slot the
      // autocomplete reserves by default would only ever render as blank space
      // between this section and the next.
      hideErrors
      fullWidth
    />
  );
}

/**
 * Confirms a mark whose canonical falls in a different reporting period. A
 * question rather than a block: the user may know something the quarter
 * comparison does not, and the alternative it names is a different action
 * (the no-action closure), so the answer has to come before the write.
 *
 * Built on ConfirmDialog, the app's are-you-sure pattern — confirm on the left
 * reading "Yes, …", Cancel on the right — rather than the form-dialog layout,
 * because this asks a question instead of collecting input.
 *
 * Shared with the bulk dialog, which is where the conflation costs most — one
 * confirmation can cover sixty rows across twenty quarters. The prose differs
 * between one row and a selection and so comes in as `children`; what is shared
 * is the part with rules behind it, the dismissal handling: every route out
 * (Cancel, the X, Escape, the backdrop) is refused while the write is in
 * flight, because dismissing mid-flight empties the picker while the write is
 * still in the air.
 */
export function DifferentPeriodDialog({
  saving,
  confirmLabel,
  onConfirm,
  onCancel,
  children,
}: {
  saving: boolean;
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  // Cancel, the X, Escape and the backdrop all route to `dismiss`, which refuses
  // while the write is in flight.
  const dismiss = () => {
    if (!saving) onCancel();
  };
  return (
    <ConfirmDialog
      open
      loading={saving}
      header="Mark as duplicate?"
      confirmText={confirmLabel}
      onConfirm={onConfirm}
      onCancel={dismiss}
      onClose={dismiss}
    >
      {/* Plain body copy, not an Alert: the text is fixed explanation, not a
          state the dialog has entered. */}
      <DialogContentText>{children}</DialogContentText>
    </ConfirmDialog>
  );
}

/**
 * The steer the confirmation exists to give, in the words both paths use: a
 * different quarter usually means a recurring failure, which the no-action
 * closure is for. Kept in one place so the two callers cannot drift on the
 * advice, only on how they describe what is being marked. The status name comes
 * from the shared label map rather than a literal, so renaming it moves this
 * copy too.
 */
export function differentPeriodAdvice(subject: string): string {
  const closure = GOV_STATUS_LABEL[HarmonizedViolationGovStatus.ClosedWontDo];
  return `Violations in different periods are usually a recurring failure rather than one event recorded twice — consider closing ${subject} as "${closure}" instead.`;
}

/** The canonical as both the grid row and the mutation carry it. */
type MarkedCanonical = NonNullable<ViolationRow["duplicateOf"]>;

/**
 * What the row was marked against, named when the caller may know it. A
 * canonical outside their scope (another facility, or held) resolves to null
 * server-side, and is left unnamed rather than reduced to a bare id they could
 * not look up anyway.
 */
function markedAsText(canonical: MarkedCanonical | null): string {
  const named = canonical ? candidateLabel(canonical) : "another violation";
  return `Marked as a duplicate of ${named}, so it stays out of the default view.`;
}

/** One violation marked as a duplicate of the row on screen, as the drawer lists it. */
export type MarkedDuplicate =
  HarmonizedViolationDuplicatesQuery["harmonizedViolationDuplicates"]["items"][number];

/**
 * How many of a canonical's duplicates the drawer names. A handful: the case
 * this section was built for is a facility with sixty of them, and the rest are
 * one click away behind "View duplicates".
 */
export const DUPLICATE_PREVIEW_LIMIT = 5;

/**
 * The state a canonical is in: other rows point at it, so it cannot itself be
 * marked a duplicate. Names the first few of them and leads to the rest.
 *
 * Presentational: `total` and `listed` must come from the SAME answer, so the
 * sentence can never caption a different number of rows than it lists.
 */
function CanonicalDuplicatesSection({
  total,
  listed,
  onViewDuplicates,
}: {
  total: number;
  listed: MarkedDuplicate[];
  onViewDuplicates: () => void;
}) {
  const notListed = Math.max(total - listed.length, 0);
  return (
    <Stack spacing={1}>
      <SectionHeading>Duplicate</SectionHeading>
      <Alert severity="info" icon={false}>
        {pluralize("violation", total, true)} {total === 1 ? "is" : "are"} marked as a duplicate of
        this one, so it cannot itself be marked as a duplicate.
      </Alert>
      {listed.length > 0 ? (
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
          {listed.map((duplicate) => (
            <Typography key={duplicate.id} component="li" variant="body2">
              {candidateLabel(duplicate)}
            </Typography>
          ))}
        </Stack>
      ) : null}
      {notListed > 0 ? (
        <Typography variant="body2" color="text.secondary">
          and {notListed} more
        </Typography>
      ) : null}
      <Box>
        <Button variant="outlined" onClick={onViewDuplicates}>
          View duplicates
        </Button>
      </Box>
    </Stack>
  );
}

/**
 * Marks this violation as a duplicate of another, or removes that mark. Search
 * runs through the dedicated candidate query, which enforces the grid's tenant
 * scope, facility access, and held-row suppression.
 *
 * Mounted with `key={violation.id}` by the drawer so picker state resets when a
 * different violation opens (mirrors ResolutionStatusEditor's keyed-no-effect
 * pattern). The drawer holds its row as a snapshot rather than live cache data,
 * so the written pointer is kept here too.
 */
export function DuplicateOfEditor({
  violation,
  tenantId,
  onViewDuplicates,
  readOnly,
}: {
  violation: ViolationRow;
  tenantId: string;
  /**
   * Called once the grid has been filtered to this canonical's duplicates. The
   * drawer closes on it rather than the editor reaching into the drawer.
   */
  onViewDuplicates: () => void;
  /** Shows the mark without letting the viewer change it. */
  readOnly: boolean;
}) {
  const alerts = useAlerts();
  const [duplicateOfId, setDuplicateOfId] = useState<string | null>(
    violation.duplicateOfId ?? null
  );
  // Named in the marked state below. Seeded from the drawer's snapshot for a row
  // that arrived already marked, and replaced by what the write answers with.
  const [canonical, setCanonical] = useState<MarkedCanonical | null>(violation.duplicateOf ?? null);
  const [candidate, setCandidate] = useState<CanonicalCandidate | null>(null);
  // The candidate waiting on the different-period question, or null when none is.
  const [confirming, setConfirming] = useState<CanonicalCandidate | null>(null);
  // A violation cannot be a duplicate of itself; the server rejects it.
  const exclude = useMemo(
    () => ({
      ids: [violation.id],
      note: "(this violation)",
      constraint: "and not this violation itself",
    }),
    [violation.id]
  );

  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();
  // Only a canonical has duplicates to list, and most rows are not one, so the
  // drawer asks nothing for them. Asked here rather than inside the branch that
  // renders it, so a fresh answer of zero can fall through to the picker: the
  // drawer's row is a snapshot, and the rule this section states is the server's.
  const { data: duplicatesData } = useQuery(HARMONIZED_VIOLATION_DUPLICATES, {
    variables: { tenantId, canonicalId: violation.id, limit: DUPLICATE_PREVIEW_LIMIT },
    skip: violation.duplicateCount === 0,
  });
  const duplicates = duplicatesData?.harmonizedViolationDuplicates;

  const [setDuplicateOf, { loading: saving }] = useMutation(SET_HARMONIZED_VIOLATION_DUPLICATE_OF, {
    // A mark changes both the visible row set (duplicates leave the default
    // view) and the counts, and Apollo can recompute neither from the returned row.
    refetchQueries: [HARMONIZED_VIOLATIONS, HARMONIZED_VIOLATION_STATS],
  });

  /** Clears the picked candidate either way, so a write that failed claims nothing. */
  const write = async (nextDuplicateOfId: string | null) => {
    try {
      const { data } = await setDuplicateOf({
        variables: { tenantId, id: violation.id, duplicateOfId: nextDuplicateOfId },
      });
      setDuplicateOfId(nextDuplicateOfId);
      // The server's own scoped answer, not the picked option: a canonical the
      // caller could not reach on its own comes back null and stays unnamed.
      setCanonical(data?.setHarmonizedViolationDuplicateOf.duplicateOf ?? null);
      setCandidate(null);
      alerts.success(nextDuplicateOfId ? "Marked as a duplicate" : "Duplicate mark removed");
    } catch (e) {
      setCandidate(null);
      // A rule the client cannot pre-empt (a canonical someone else marked a
      // duplicate meanwhile) refuses with its own wording, and an identical
      // retry would fail the same way — so say what the server said when it
      // said anything. Matches the bulk path's handling of the same refusals.
      const generic = nextDuplicateOfId
        ? "Could not mark the violation as a duplicate. Try again."
        : "Could not remove the duplicate mark. Try again.";
      alerts.error(getUserFacingMessage(e) ?? generic, e);
    }
  };

  // Picking is the write, as it is for the resolution status and assignee
  // pickers in the same panel — except across reporting periods, where the
  // question of whether this is a duplicate at all comes first.
  const handlePick = (picked: CanonicalCandidate | null) => {
    setCandidate(picked);
    if (!picked) return;
    if (inDifferentReportingPeriods(violation.violationStartDate, picked.violationStartDate)) {
      setConfirming(picked);
      return;
    }
    void write(picked.id);
  };

  if (duplicateOfId) {
    return (
      <Stack spacing={1}>
        <SectionHeading>Duplicate</SectionHeading>
        <Alert severity="info" icon={false}>
          {markedAsText(canonical)}
        </Alert>
        {readOnly ? null : (
          <Box>
            <Button variant="outlined" disabled={saving} onClick={() => void write(null)}>
              Remove duplicate mark
            </Button>
          </Box>
        )}
      </Stack>
    );
  }

  // The server rejects marking a row that is itself a canonical (the one-level
  // rule from the other direction), so the picker is not offered at all. The
  // row's own count stands in only until the drawer's answer lands.
  const canonicalOf = duplicates?.count ?? violation.duplicateCount;
  if (canonicalOf > 0) {
    return (
      <CanonicalDuplicatesSection
        total={canonicalOf}
        listed={duplicates?.items ?? []}
        onViewDuplicates={() => {
          // The grid drill-down the count badge performs, from the drawer. The
          // drawer closes with it: a re-filtered grid behind an open drawer
          // reads as nothing having happened.
          setOmnisearchImmediate(duplicatesOfOmnisearch(omnisearch, violation.id));
          onViewDuplicates();
        }}
      />
    );
  }

  return (
    <Stack spacing={1}>
      <SectionHeading>Duplicate</SectionHeading>
      <CanonicalPicker
        tenantId={tenantId}
        facilityId={violation.facility.id}
        program={violation.sourceProgram}
        exclude={exclude}
        value={candidate}
        onChange={handlePick}
        disabled={saving || readOnly}
      />
      {confirming ? (
        <DifferentPeriodDialog
          saving={saving}
          confirmLabel="Yes, mark as duplicate"
          // Kept open while the write is in flight, which is what disables its
          // Cancel and turns the primary button into its saving state.
          onConfirm={async () => {
            await write(confirming.id);
            setConfirming(null);
          }}
          // Declining empties the picker: a candidate left sitting there could be
          // committed later by an unrelated interaction the user never tied to it.
          onCancel={() => {
            setConfirming(null);
            setCandidate(null);
          }}
        >
          That violation is in a different reporting period. {differentPeriodAdvice("this one")} You
          can still mark it a duplicate.
        </DifferentPeriodDialog>
      ) : null}
    </Stack>
  );
}
